import { walletTypes, type Category, type Contact, type Currency, type Debt, type Goal, type Wallet, type WalletType } from '@fin/shared';
import { useMemo } from 'react';
import { useQuery } from './query.ts';

export const useWallets = () => useQuery<Wallet[]>('/wallets');
export const useCategories = () => useQuery<Category[]>('/categories');
export const useContacts = () => useQuery<Contact[]>('/contacts');
export const useDebts = () => useQuery<Debt[]>('/debts');
export const useGoals = () => useQuery<Goal[]>('/goals');
export const useCurrencies = () => useQuery<Currency[]>('/currencies');

export const sortCats = (list: Category[]) => [...list].sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name, 'ru'));

/** wallet groups in the user's order; types not chosen yet follow in the default order */
export function walletTypeOrder(chosen: WalletType[] | undefined): WalletType[] {
  const rest = walletTypes.filter((t) => !chosen?.includes(t));
  return [...(chosen ?? []).filter((t) => walletTypes.includes(t)), ...rest];
}

/** wallets as the wallets page shows them: by group order, then by the sort inside the group */
export function orderWallets(list: Wallet[], chosen: WalletType[] | undefined): Wallet[] {
  const types = walletTypeOrder(chosen);
  return [...list].sort((a, b) => types.indexOf(a.type) - types.indexOf(b.type) || a.sort - b.sort || a.id - b.id);
}

export interface CategoryNode { cat: Category; children: Category[] }

/** categories as a one-level tree (parents with their subcategories), in display order */
export function categoryTree(list: Category[]): CategoryNode[] {
  const byId = new Map(list.map((c) => [c.id, c]));
  const parents = sortCats(list.filter((c) => !c.parentId || !byId.has(c.parentId)));
  return parents.map((cat) => ({ cat, children: sortCats(list.filter((c) => c.parentId === cat.id)) }));
}

/** «Покупки · Одежда» for a subcategory, just the name otherwise */
export function categoryLabel(c: Category, all: Category[]) {
  const parent = c.parentId ? all.find((x) => x.id === c.parentId) : undefined;
  return parent ? `${parent.name} · ${c.name}` : c.name;
}

export interface RatesResponse {
  base: string;
  latest: { currency: string; rate: number; date: string }[];
  history: { id: number; currency: string; date: string; rate: number }[];
}
export function useRates() {
  const q = useQuery<RatesResponse>('/rates');
  const map = useMemo(() => {
    const m = new Map<string, number>();
    if (q.data) {
      m.set(q.data.base, 1);
      for (const r of q.data.latest) m.set(r.currency, r.rate);
    }
    return m;
  }, [q.data]);
  const toBase = (amount: number, currency: string) => (map.has(currency) ? amount * map.get(currency)! : null);
  return { ...q, map, toBase, base: q.data?.base ?? 'RUB' };
}
